import { ColumnDef } from '@tanstack/react-table';
import { memo, useMemo } from 'react';
import { MenuTrigger } from 'react-aria-components';

import { Button, Menu, MenuItem, Popover, Table, Text } from '@/components/core';
import { AddIcon, CalendarCheckedIcon, ChevronIcon, SortIcon } from '@/components/icons';
import { SortingFilter, useInboxStore } from '@/store';

import { AscendingCard } from './AscendingCard.tsx';
import { DescendingCard } from './DescendingCard.tsx';
import { SortColumnCard } from './SortColumnCard.tsx';

export type InboxSortProps = object;

// eslint-disable-next-line no-empty-pattern
const IInboxSort = ({}: InboxSortProps) => {
  const sorting = useInboxStore((state) => state.sorting);
  const updateSorting = useInboxStore((state) => state.updateSorting);
  const moveSorting = useInboxStore((state) => state.moveSorting);

  const availableSorting = useMemo(() => sorting.filter((s) => s.hidden), [sorting]);

  const columns = useMemo<ColumnDef<SortingFilter>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'Column',
        cell: ({ row }) => {
          return (
            <div className="pr-2">
              <MenuTrigger>
                <Button isDisabled={availableSorting.length === 0} className="w-full" variant="secondary">
                  <div className="gap-sm flex w-full items-center">
                    <CalendarCheckedIcon />
                    <Text>{row.original.name ?? row.original.id}</Text>
                    <div className="grow" />
                    <ChevronIcon rotation="down" />
                  </div>
                </Button>
                <Popover>
                  <Menu>
                    {availableSorting.map((s) => (
                      <MenuItem
                        key={s.id}
                        onAction={() =>
                          updateSorting({
                            [row.original.id]: { hidden: true },
                            [s.id]: { hidden: false, desc: row.original.desc },
                          })
                        }
                      >
                        <SortColumnCard name={s.name ?? s.id} />
                      </MenuItem>
                    ))}
                  </Menu>
                </Popover>
              </MenuTrigger>
            </div>
          );
        },
      },
      {
        accessorKey: 'sorting',
        header: 'Sorting',
        cell: ({ row }) => {
          return (
            <MenuTrigger>
              <Button className="w-full" variant="secondary">
                {row.original.desc ? (
                  <DescendingCard>
                    <div className="grow" />
                    <ChevronIcon rotation="down" />
                  </DescendingCard>
                ) : (
                  <AscendingCard>
                    <div className="grow" />
                    <ChevronIcon rotation="down" />
                  </AscendingCard>
                )}
              </Button>
              <Popover>
                <Menu>
                  <MenuItem onAction={() => updateSorting({ [row.original.id]: { desc: false } })}>
                    <AscendingCard />
                  </MenuItem>
                  <MenuItem onAction={() => updateSorting({ [row.original.id]: { desc: true } })}>
                    <DescendingCard />
                  </MenuItem>
                </Menu>
              </Popover>
            </MenuTrigger>
          );
        },
      },
    ],
    [availableSorting, updateSorting]
  );

  const hasSorting = sorting.filter((s) => !s.hidden).length > 0;

  return (
    <MenuTrigger>
      <Button className="hover:bg-gray-100" variant="plain">
        <SortIcon className={hasSorting ? '!text-primary-500' : '!text-gray-400'} />
      </Button>
      <Popover className="flex-col">
        <Table
          className="border-separate border-spacing-x-0 border-spacing-y-2 px-6"
          columns={columns}
          data={sorting}
          deleteRowAction={(row) => updateSorting({ [row.original.id]: { hidden: true } })}
          onRowIndexChange={(oldIndex, newIndex) => moveSorting(oldIndex, newIndex)}
          hideHeader
        />
        <MenuTrigger>
          <Button isDisabled={availableSorting.length === 0} className="w-fit" variant="plain">
            <AddIcon />
            <Text>Add sort</Text>
          </Button>
          <Popover>
            <Menu>
              {availableSorting.map((s) => (
                <MenuItem key={s.id} onAction={() => updateSorting({ [s.id]: { hidden: false } })}>
                  <SortColumnCard name={s.name ?? s.id} />
                </MenuItem>
              ))}
            </Menu>
          </Popover>
        </MenuTrigger>
      </Popover>
    </MenuTrigger>
  );
};

export const InboxSort = memo(IInboxSort) as typeof IInboxSort;
